import * as MUI from "@mui/material";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SimpleAlert from "../../../components/display/SimpleAlert";
import UserHint from "../../../components/display/UserHint";
import CharCountField from "../../../components/form/CharCountField";
import { AppointmentType } from "../../../components/modals/bookingModalComponents/context/BookingModalContext";
import { PRICES_BASE_REQUEST } from "../../../enums/ApiPaths";
import { SERVICES } from "../../../enums/QueryKeys";
import { useFetch } from "../../../hooks/Fetching";
import useUser from "../../../hooks/useUser";
import { mandatory } from "../../../utils/Functions";
import { formatDurationLabel } from "../../admin/clinic/components/warehouse/components/services/components/ServiceDurationForm";

export default function AppointmentTypePicker({ labelOff, rows = 4, testId }) {
  const { t, i18n } = useTranslation();
  const {
    control,
    watch,
    formState: { errors },
    clearErrors,
    setError,
    setValue,
  } = useFormContext();

  const { currentClinic, roles } = useUser();
  const isBasicTier = currentClinic.basicTier;

  const {
    data: services,
    isLoading: loadingServices,
    error: servicesError,
  } = useFetch(PRICES_BASE_REQUEST, currentClinic?.id, SERVICES, !isBasicTier);

  const [translatedServices, setTranslatedServices] = React.useState([]);
  const selectedValue = watch(AppointmentType); // Watch for Chip display

  React.useEffect(() => {
    if (services) {
      const currentLang = i18n.language;
      const translation = services.map((service) => ({
        id: service.id,
        mandatroyDesc: service?.mandatoryDescription,
        title: service.translations[currentLang] || service.title,
        duration: service?.duration ?? 30, //30 should probably go into a seperate variable. as default service duration
      }));
      setTranslatedServices(translation);

      //Preselecting service if there is only one
      if (Number(translation?.length) === 1) {
        const singleType = translation[0];
        setValue(AppointmentType, singleType);
      }
    }
  }, [services, i18n.language]);

  //I don't like this, but it's the best for now until we figure out how to configue zod to validate it
  //I'll open up a ticket to investigate how to validate all of these objects individaully
  //This approach is really performance tasking since useEffect rerenders the entire page every time we update the title
  React.useEffect(() => {
    const selected = watch(AppointmentType);
    const title = selected?.title;

    if (selected != undefined && selected != null) {
      if (title?.length > 50) {
        setError(
          AppointmentType,
          { type: "focus", message: t("IllegalLength") + 50 },
          { shouldFocus: true },
        );
      } else {
        clearErrors(AppointmentType);
      }
    }
  }, [watch(AppointmentType)?.title]);

  if (loadingServices) {
    return <MUI.LinearProgress />;
  } else if (servicesError || !translatedServices) {
    return (
      <SimpleAlert
        severity={"error"}
        message={"ErrorLoadingServices"}
        testId={testId && `${testId}.error-alert`}
      />
    );
  }

  if (!isBasicTier && !roles.adminOrEmployee) {
    if ((!services || services?.length === 0) && !loadingServices) {
        return (
          <SimpleAlert
            severity={"warning"}
            message={"NoServices"}
            testId={testId && `${testId}.no-services-alert`}
          />
        );
      }
  }

  function isRequired() {
    if (roles?.adminOrEmployee) return false;
    if (isBasicTier) return true;

    const selected = watch(AppointmentType);
    return selected?.mandatroyDesc ? true : false;
  }

  const isOne = services?.length <= 1;
  const disabledCondition = isOne && !roles.adminOrEmployee;

  if (isBasicTier) {
    return (
      <CharCountField
        required={isRequired()}
        fullWidth
        showInfoChip={isRequired()}
        infoChipLabel="AddDescriptionToServiceUserHint"
        name={"description"}
        label={t("DescribeTheProblem")}
        multiline
        rows={rows}
        testId={testId && `${testId}.description-input`}
      />
    );
  } else {
    return (
      <MUI.Stack spacing={disabledCondition ? 0 : 2}>
        <Controller
          fullWidth
          disabled={loadingServices}
          name={AppointmentType}
          control={control}
          rules={{ required: t("SelectValue") }}
          render={({ field }) => (
            <MUI.FormControl fullWidth error={!!errors[AppointmentType]}>
              <MUI.Autocomplete
                {...field}
                disabled={disabledCondition}
                freeSolo={roles.adminOrEmployee}
                autoSelect
                onBlur={() => {
                  const enteredText = field.value?.title ?? field.value;
                  const match = translatedServices.find(
                    (option) => option.title === enteredText,
                  );

                  if (!match) {
                    if (!roles.adminOrEmployee) {
                      field.onChange(null);
                    }
                  }
                }}
                value={field.value ?? null}
                onChange={(e, value) => {
                  const enteredText =
                    typeof value === "string" ? value : value?.title;
                  const match = translatedServices.find(
                    (option) => option.title === enteredText,
                  );

                  if (match) {
                    field.onChange(match);
                  } else if (typeof value === "string") {
                    if (roles.adminOrEmployee) {
                      field.onChange({ id: null, title: value });
                    } else {
                      field.onChange(null);
                    }
                  } else {
                    field.onChange(null);
                  }
                  setValue("time", null);
                  setValue("endTime", null);
                  clearErrors(["time", "endTime"]);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                options={translatedServices}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.title
                }
                renderInput={(params) => {
                  return (
                    <MUI.TextField
                      {...params}
                      label={labelOff ? null : mandatory(t("Service"))}
                      error={!!errors[AppointmentType]}
                      inputProps={{
                        ...params.inputProps,
                        ...(testId && {
                          "data-testid": `${testId}.service-select`,
                        }),
                      }}
                    />
                  );
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.id || option.title}
                    data-testid={testId && `${testId}.service-select.option.${option.id}`}
                  >
                    {option.title}
                    <MUI.Chip
                      label={`${formatDurationLabel(option.duration)}`}
                      color="primary"
                      size="small"
                      sx={{ ml: 2 }}
                      data-testid={
                        testId && `${testId}.service-select.option.${option.id}.duration`
                      }
                    />
                  </li>
                )}
                clearText={t("Clear")}
                closeText={t("Close")}
                openText={t("Open")}
              />
              {selectedValue?.duration && (
                <MUI.FormHelperText>
                  <MUI.Box display="flex" alignItems="center" gap={1} mt={1}>
                    <MUI.Typography variant="caption" color="text.secondary">
                      {t("Duration")}:
                    </MUI.Typography>
                    <MUI.Chip
                      label={formatDurationLabel(selectedValue.duration)}
                      size="small"
                      color="primary"
                      variant="filled"
                    />
                  </MUI.Box>
                </MUI.FormHelperText>
              )}
              <MUI.FormHelperText>
                {errors[AppointmentType]?.message}
              </MUI.FormHelperText>
            </MUI.FormControl>
          )}
        />

        {disabledCondition ? (
          <UserHint
            sx={{ mb: 2 }}
            hint={"OnlyOneServicePreselectedUserHint"}
            data-testid={testId && `${testId}.single-service-hint`}
          />
        ) : null}

        <CharCountField
          required={isRequired()}
          fullWidth
          showInfoChip={isRequired()}
          infoChipLabel="AddDescriptionToServiceUserHint"
          name={"description"}
          label={t("DescribeTheProblem")}
          multiline
          rows={rows}
          testId={testId && `${testId}.description-input`}
        />
      </MUI.Stack>
    );
  }
}
